// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Manifest maths — the pull-based sync brain. Both peers exchange a manifest
 * (path → size + mtime). Everything is pull-driven: a peer looks at the OTHER
 * side's manifest and decides what IT wants, then asks for it. Nobody pushes
 * unsolicited, so a file is never sent twice and the two sides never race.
 *
 * Syncwell never deletes: a path that vanishes from a peer's manifest is simply
 * no longer offered — your existing files are left untouched. That safety
 * guarantee is deliberate and advertised in the UI.
 *
 * mtime is the version clock. A file is wanted when the remote has it and we
 * either lack it or hold a strictly older copy. An equal-mtime/different-size
 * pair is a genuine conflict; we skip it rather than clobber either side.
 */
import type { Direction, FileEntry, Manifest } from './types.ts';

/** Serialise a manifest to a plain JSON-safe array for the wire. */
export function serializeManifest(m: Manifest): FileEntry[] {
  return Array.from(m.values()).map((e) => ({ path: e.path, size: e.size, mtime: e.mtime }));
}

/** Rebuild a manifest map from the wire form, dropping any malformed entries. */
export function deserializeManifest(arr: unknown): Manifest {
  const m: Manifest = new Map();
  if (!Array.isArray(arr)) return m;
  for (const raw of arr) {
    if (!raw || typeof raw !== 'object') continue;
    const e = raw as Record<string, unknown>;
    if (typeof e.path !== 'string') continue;
    if (typeof e.size !== 'number' || !Number.isFinite(e.size) || e.size < 0) continue;
    if (typeof e.mtime !== 'number' || !Number.isFinite(e.mtime)) continue;
    m.set(e.path, { path: e.path, size: e.size, mtime: e.mtime });
  }
  return m;
}

/** True if the receiving direction lets us pull files in. */
export function canReceive(dir: Direction): boolean {
  return dir === 'both' || dir === 'receive';
}

/** True if the sending direction lets us serve files out. */
export function canSend(dir: Direction): boolean {
  return dir === 'both' || dir === 'send';
}

export interface Wanted {
  /** Paths we should request from the remote (they are newer or unique). */
  request: string[];
  /** Paths present on both with equal mtime but different size — flagged, not synced. */
  conflicts: string[];
}

/**
 * Given our local manifest and the remote's, decide what we want to pull.
 * Pure and deterministic — the heart of the engine and the most-tested function.
 */
export function computeWanted(local: Manifest, remote: Manifest, dir: Direction): Wanted {
  const request: string[] = [];
  const conflicts: string[] = [];
  if (!canReceive(dir)) return { request, conflicts };

  for (const [path, r] of remote) {
    const l = local.get(path);
    if (!l) {
      request.push(path); // remote-only → pull
    } else if (r.mtime > l.mtime) {
      request.push(path); // remote strictly newer → pull
    } else if (r.mtime === l.mtime && r.size !== l.size) {
      conflicts.push(path); // same clock, different bytes → do not clobber
    }
    // else: we are equal or newer → nothing to pull
  }
  request.sort();
  conflicts.sort();
  return { request, conflicts };
}

/**
 * On receiving a request, decide which of the asked-for paths we will actually
 * serve: only ones we still hold, only if our direction allows sending.
 */
export function fulfillable(local: Manifest, requested: string[], dir: Direction): string[] {
  if (!canSend(dir)) return [];
  const out: string[] = [];
  for (const p of requested) if (local.has(p)) out.push(p);
  return out;
}

/** Paths whose (size, mtime) changed between two local scans — i.e. edits to re-announce. */
export function changedLocally(prev: Manifest, next: Manifest): boolean {
  if (prev.size !== next.size) return true;
  for (const [path, n] of next) {
    const p = prev.get(path);
    if (!p || p.size !== n.size || p.mtime !== n.mtime) return true;
  }
  return false;
}

/** Sum of plaintext bytes across a manifest — for the folder summary line. */
export function totalBytes(m: Manifest): number {
  let n = 0;
  for (const e of m.values()) n += e.size;
  return n;
}
